import type { ReactNode } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';

export interface ChannelValidationResult {
    input: string;
    normalized: string;
    valid_format: boolean;
    // true  = telegram resolved the handle
    // false = handle is well-formed but not occupied
    // null  = couldn't check (session unavailable / rate-limited) — caller may
    //         still allow an "add anyway" override
    exists: boolean | null;
    kind?: 'channel' | 'supergroup' | 'group' | 'user';
    title?: string;
    reason?: string;
}

interface Props {
    open: boolean;
    loading: boolean;
    result: ChannelValidationResult | null;
    onConfirm: () => void;
    onRetry: () => void;
    onCancel: () => void;
}

export default function ChannelValidationModal({
    open, loading, result, onConfirm, onRetry, onCancel,
}: Props) {
    if (!open) return null;

    const handle = result?.normalized || result?.input || '';

    let body: ReactNode;
    let actions: ReactNode;

    if (loading) {
        body = (
            <div className="flex items-center gap-3">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                <div>
                    <div className="text-base font-medium">Checking @{handle} on Telegram…</div>
                    <div className="text-sm text-muted-foreground">Verifying the channel exists.</div>
                </div>
            </div>
        );
        actions = (
            <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                Cancel
            </button>
        );
    } else if (!result) {
        // Defensive fallback — caller should not render with no result + not loading
        return null;
    } else if (!result.valid_format) {
        body = (
            <div className="flex items-start gap-3">
                <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                <div>
                    <div className="text-base font-medium">Invalid handle format</div>
                    <div className="text-sm text-muted-foreground mt-1">
                        {result.reason || 'Enter a Telegram username like @example or a t.me link.'}
                    </div>
                </div>
            </div>
        );
        actions = (
            <>
                <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                    Cancel
                </button>
                <button onClick={onRetry} className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground hover:opacity-90">
                    Retry
                </button>
            </>
        );
    } else if (result.exists === true) {
        const kindLabel = result.kind ? result.kind.charAt(0).toUpperCase() + result.kind.slice(1) : 'Channel';
        body = (
            <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-500 shrink-0 mt-0.5" />
                <div>
                    <div className="text-base font-medium">Adding channel @{handle}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                        {result.title ? `${kindLabel} · ${result.title}` : kindLabel}
                    </div>
                </div>
            </div>
        );
        actions = (
            <>
                <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                    Cancel
                </button>
                <button onClick={onConfirm} className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700">
                    Add Now
                </button>
            </>
        );
    } else if (result.exists === false) {
        body = (
            <div className="flex items-start gap-3">
                <XCircle className="w-6 h-6 text-red-500 shrink-0 mt-0.5" />
                <div>
                    <div className="text-base font-medium">Channel @{handle} not found</div>
                    <div className="text-sm text-muted-foreground mt-1">
                        {result.reason || 'No public channel with that handle exists on Telegram.'}
                    </div>
                </div>
            </div>
        );
        actions = (
            <>
                <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                    Cancel
                </button>
                <button onClick={onRetry} className="px-3 py-1.5 text-sm rounded bg-primary text-primary-foreground hover:opacity-90">
                    Retry
                </button>
            </>
        );
    } else {
        // exists === null — couldn't verify (session unavailable or rate-limited)
        body = (
            <div className="flex items-start gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                <div>
                    <div className="text-base font-medium">Couldn't verify @{handle}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                        {result.reason || 'Telegram is unreachable right now.'} You can still add it; the next scrape will try to resolve it.
                    </div>
                </div>
            </div>
        );
        actions = (
            <>
                <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                    Cancel
                </button>
                <button onClick={onRetry} className="px-3 py-1.5 text-sm rounded border border-border hover:bg-accent">
                    Retry
                </button>
                <button onClick={onConfirm} className="px-3 py-1.5 text-sm rounded bg-amber-600 text-white hover:bg-amber-700">
                    Add Anyway
                </button>
            </>
        );
    }

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
        >
            <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-2xl">
                {body}
                <div className="mt-5 flex justify-end gap-2">
                    {actions}
                </div>
            </div>
        </div>
    );
}
